import { createContext, useContext, useState, useCallback, useRef, useMemo, ReactNode } from 'react';

interface SettingsContextValue {
  onSave?: () => Promise<void>;
  isSaving: boolean;
  saveMessage: string | null;
  saveError: string | null;
  setSaveHandler: (handler: () => Promise<void>) => void;
  setIsSaving: (saving: boolean) => void;
  setSaveMessage: (message: string | null) => void;
  setSaveError: (error: string | null) => void;
}

const SettingsContext = createContext<SettingsContextValue | undefined>(undefined);

export function SettingsProvider({ children }: { children: ReactNode }) {
  const handlerRef = useRef<(() => Promise<void>) | undefined>(undefined);
  const [hasHandler, setHasHandler] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  const setSaveHandler = useCallback((handler: () => Promise<void>) => {
    const hadHandler = handlerRef.current !== undefined;
    handlerRef.current = handler;
    // Only update hasHandler state if it changed from undefined to defined
    if (!hadHandler && handler !== undefined) {
      setHasHandler(true);
    }
  }, []);

  // Create a stable reference to the handler that consumers can use
  const onSave = useCallback(async () => {
    if (handlerRef.current) {
      return await handlerRef.current();
    }
  }, []);

  // Memoize context value to prevent unnecessary re-renders
  const contextValue = useMemo(() => ({
    onSave: hasHandler ? onSave : undefined,
    isSaving,
    saveMessage,
    saveError,
    setSaveHandler,
    setIsSaving,
    setSaveMessage,
    setSaveError,
  }), [hasHandler, onSave, isSaving, saveMessage, saveError, setSaveHandler]);

  return (
    <SettingsContext.Provider value={contextValue}>
      {children}
    </SettingsContext.Provider>
  );
}

export function useSettingsContext(): SettingsContextValue {
  const context = useContext(SettingsContext);
  if (context === undefined) {
    // Return default values instead of throwing error
    return {
      onSave: undefined,
      isSaving: false,
      saveMessage: null,
      saveError: null,
      setSaveHandler: () => {},
      setIsSaving: () => {},
      setSaveMessage: () => {},
      setSaveError: () => {},
    };
  }
  return context;
}

